import type { ConstantsBillingUsageItem } from "@gosaas/types";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import { IconCurrencyDollar, IconDeviceDesktop } from "@tabler/icons-react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { InDepthUsageSection } from "@/components/billing/in-depth-usage-section";
import { KeyMetricsOverview } from "@/components/billing/key-metrics-overview";
import { UsageRangeControls } from "@/components/billing/usage-range-controls";
import { UsageTrendsChart } from "@/components/billing/usage-trends-chart";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import {
	BillingUsageRangeProvider,
	useBillingUsageRange,
} from "@/components/providers/billing-usage-range";
import { useTeam } from "@/components/providers/team";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
	type BillingUsageItem,
	filterBillingUsageForDisplay,
	formatUsageHeaderDateRange,
	formatUsagePeriodBadgeLabel,
	shouldFetchBillingUsage,
} from "@/lib/billing-usage";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export const Route = createFileRoute("/_authorized/team/billing/usage")({
	component: () => (
		<BillingUsageRangeProvider>
			<Usage />
		</BillingUsageRangeProvider>
	),
});

export type { BillingUsageItem };

export function Usage() {
	const { teamID, teams, loading } = useTeam();
	const router = useRouter();
	const [usage, setUsage] = useState<BillingUsageItem[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { range, start, end } = useBillingUsageRange();

	const team = teams.find((t) => t.id === teamID);
	const canReadBilling = hasPermission(
		team?.role as Role,
		"billing:read" as Permission,
	);

	useEffect(() => {
		if (canReadBilling === false) {
			toast.warning("You do not have permission to view billing usage", {
				description: "Please contact support if you believe this is an error.",
			});
			router.navigate({ to: "/403" });
		}
	}, [canReadBilling, router]);

	useEffect(() => {
		const fetchUsage = async () => {
			if (!shouldFetchBillingUsage(teamID, canReadBilling, range, start, end)) {
				return;
			}
			if (!teamID) {
				return;
			}
			setIsLoading(true);
			try {
				const result = await requestWithHandling<ConstantsBillingUsageItem[]>(
					() =>
						apiClient.api.billingUsageList(teamID, {
							range: range,
							start: start ?? undefined,
							end: end ?? undefined,
						}),
				);
				if (result.code !== "SUCCESS") {
					throw new Error(result.message);
				}
				setUsage(filterBillingUsageForDisplay(result.data ?? []));
			} catch (error) {
				if (
					error instanceof TypedClientApiError &&
					error.code === "BILLING_NOT_FOUND"
				) {
					setUsage([]);
				} else {
					toast.error("Failed to load usage", {
						description:
							error instanceof TypedClientApiError
								? error.message
								: "An unknown error occurred.",
					});
				}
			} finally {
				setIsLoading(false);
			}
		};
		fetchUsage();
	}, [teamID, canReadBilling, range, start, end]);

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Team", href: null },
					{ title: "Billing", href: "/team/billing" },
					{ title: "Usage", href: "/team/billing/usage" },
				]}
				title="Usage Dashboard"
				description="Monitor and manage your team's feature usage and billing"
				icon={
					<IconCurrencyDollar className="size-6 text-primary" aria-hidden />
				}
				after={
					<div className="flex flex-col gap-4 rounded-lg border border-border bg-muted/30 p-4 shadow-sm sm:flex-row sm:items-start sm:justify-between lg:items-center">
						<div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
							<Badge
								variant="outline"
								className="w-fit px-3 py-2 font-medium sm:py-1.5"
							>
								{formatUsagePeriodBadgeLabel(range)}
							</Badge>
							<div className="hidden h-4 w-px shrink-0 bg-border sm:block" />
							<span className="min-w-0 text-base/7 font-medium text-foreground sm:text-sm/6">
								{formatUsageHeaderDateRange(
									range,
									start,
									end,
									usage?.[0] ?? null,
								)}
							</span>
						</div>
						{team?.billing.is_active && (
							<UsageRangeControls className="w-full min-w-0 sm:w-auto sm:max-w-sm" />
						)}
					</div>
				}
			/>

			{loading || isLoading ? (
				<Loader />
			) : (
				<div className="space-y-10">
					{usage && usage.length > 0 && (
						<>
							<KeyMetricsOverview data={usage} />
							<UsageTrendsChart data={usage} />
							<InDepthUsageSection data={usage} loading={isLoading} />
						</>
					)}

					{(!usage || usage.length === 0) && (
						<Card className="border border-border bg-card shadow-sm">
							<CardContent className="py-20 text-center">
								<div className="mb-6 inline-flex size-24 items-center justify-center rounded-full bg-linear-to-br from-primary/10 to-primary/5">
									<IconDeviceDesktop className="size-12 text-primary opacity-60" />
								</div>
								<h3 className="mb-3 text-2xl font-bold text-foreground">
									No usage data available
								</h3>
								<p className="mx-auto max-w-[40ch] text-pretty text-base/7 text-muted-foreground sm:text-base/7">
									{range === "custom"
										? "No data found for the selected period. Try selecting a different date range."
										: "Start using features to see your usage statistics here. Your data will appear automatically as you use different features."}
								</p>
							</CardContent>
						</Card>
					)}
				</div>
			)}
		</PageLayout>
	);
}
